<script setup>
/**
 * GviaAddContactModal
 * Full-screen modal (mobile/tablet) / centered dialog (desktop)
 * for adding a contact person to a client in Priority ERP
 */
import { ref, computed } from 'vue'
import { httpsCallable } from 'firebase/functions'
import { getFirebaseFunctions } from '@/core/firebase/firebase'
import { useBackButtonClose } from '@/core/composables/useBackButtonClose'

const props = defineProps({
  clientMakat: { type: String, required: true },
})

const emit = defineEmits(['close', 'contact-added'])

// Back button intercept
const isOpen = ref(true)
useBackButtonClose(isOpen, () => emit('close'))

// Form state
const name = ref('')
const email = ref('')
const phone = ref('')

// Loading / error
const submitting = ref(false)
const errorMsg = ref('')

// Validation
const isValidEmail = computed(() => {
  if (!email.value) return false
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value.trim())
})

const isFormValid = computed(() => {
  return name.value.trim() && isValidEmail.value && phone.value.trim()
})

// Submit
async function handleSubmit() {
  if (!isFormValid.value || submitting.value) return

  submitting.value = true
  errorMsg.value = ''

  try {
    const functions = getFirebaseFunctions()
    const callable = httpsCallable(functions, 'priorityUpdateClientContact')
    await callable({
      makat: props.clientMakat,
      name: name.value.trim(),
      phone: phone.value.trim(),
      email: email.value.trim(),
    })

    emit('contact-added')
  } catch (err) {
    console.error('[GviaAddContactModal] Error:', err)
    errorMsg.value = err.message || 'שגיאה בהוספת איש קשר'
  } finally {
    submitting.value = false
  }
}
</script>

<template>
  <Teleport to="body">
    <Transition name="modal">
      <div class="modal-overlay" @click.self="emit('close')">
        <div class="modal-container">
          <!-- Header -->
          <div class="modal-header">
            <h2 class="modal-title">הוספת איש קשר</h2>
            <button class="close-btn" @click="emit('close')">
              <svg
                viewBox="0 0 24 24"
                width="24"
                height="24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          <!-- Form -->
          <div class="modal-body">
            <p class="modal-desc">הוסף איש קשר ללקוח {{ clientMakat }} בפריורטי</p>

            <div class="form-group">
              <label class="form-label" for="contact-name">שם איש הקשר</label>
              <input
                id="contact-name"
                v-model="name"
                type="text"
                class="form-input"
                placeholder="שם מלא"
                :disabled="submitting"
              />
            </div>

            <div class="form-group">
              <label class="form-label" for="contact-email">אימייל</label>
              <input
                id="contact-email"
                v-model="email"
                type="email"
                class="form-input"
                placeholder="example@email.com"
                dir="ltr"
                :disabled="submitting"
              />
            </div>

            <div class="form-group">
              <label class="form-label" for="contact-phone">פלאפון</label>
              <input
                id="contact-phone"
                v-model="phone"
                type="tel"
                class="form-input"
                placeholder="050-0000000"
                dir="ltr"
                :disabled="submitting"
                @keypress.enter="handleSubmit"
              />
            </div>

            <!-- Error -->
            <div v-if="errorMsg" class="error-message">
              <svg
                viewBox="0 0 24 24"
                width="18"
                height="18"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <span>{{ errorMsg }}</span>
            </div>
          </div>

          <!-- Footer -->
          <div class="modal-footer">
            <button class="cancel-btn" :disabled="submitting" @click="emit('close')">ביטול</button>
            <button
              class="submit-btn"
              :class="{ active: isFormValid }"
              :disabled="!isFormValid || submitting"
              @click="handleSubmit"
            >
              <template v-if="submitting">
                <span class="spinner"></span>
                שומר...
              </template>
              <template v-else>שמור איש קשר</template>
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: var(--bg-primary);
  display: flex;
  align-items: stretch;
  justify-content: center;
  z-index: 9999;
}

.modal-container {
  background: var(--bg-primary);
  width: 100%;
  height: 100%;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

/* Desktop: centered dialog */
@media (min-width: 1280px) {
  .modal-overlay {
    background: rgba(0, 0, 0, 0.5);
    align-items: center;
  }

  .modal-container {
    max-width: 500px;
    height: auto;
    max-height: 90vh;
    border-radius: var(--border-radius-lg);
    box-shadow: var(--shadow-xl, 0 20px 60px rgba(0, 0, 0, 0.3));
  }
}

/* Header */
.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-lg);
  border-bottom: 1px solid var(--border-color);
}

.modal-title {
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
  margin: 0;
}

.close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: var(--bg-secondary);
  border: none;
  border-radius: 50%;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.close-btn:hover {
  background: var(--bg-tertiary);
  color: var(--text-primary);
}

/* Body */
.modal-body {
  flex: 1;
  padding: var(--spacing-lg);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
}

.modal-desc {
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
  margin: 0;
  text-align: center;
}

/* Form */
.form-group {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
}

.form-label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--text-secondary);
}

.form-input {
  padding: var(--spacing-md);
  font-size: var(--font-size-base);
  border: 2px solid var(--border-color);
  border-radius: var(--border-radius);
  background: var(--bg-primary);
  color: var(--text-primary);
  font-family: var(--font-family);
  transition: border-color var(--transition-fast);
}

.form-input:focus {
  outline: none;
  border-color: var(--color-primary);
}

.form-input:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Error */
.error-message {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-md);
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid var(--color-error);
  border-radius: var(--border-radius);
  color: var(--color-error);
  font-size: var(--font-size-sm);
}

/* Footer */
.modal-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--spacing-sm);
  padding: var(--spacing-md) var(--spacing-lg);
  border-top: 1px solid var(--border-color);
  background: var(--bg-secondary);
}

.cancel-btn {
  padding: var(--spacing-sm) var(--spacing-lg);
  background: transparent;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  color: var(--text-secondary);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-medium);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.cancel-btn:hover {
  background: var(--bg-tertiary);
  color: var(--text-primary);
}

.cancel-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.submit-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-lg);
  background: var(--bg-tertiary);
  border: none;
  border-radius: var(--border-radius);
  color: var(--text-muted);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  font-family: var(--font-family);
  cursor: not-allowed;
  transition: all var(--transition-fast);
}

.submit-btn.active {
  background: var(--color-primary);
  color: white;
  cursor: pointer;
}

.submit-btn.active:hover {
  background: var(--color-primary-hover);
}

.submit-btn.active:active {
  transform: scale(0.97);
}

.submit-btn:disabled {
  cursor: not-allowed;
}

/* Spinner */
.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Transition */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.25s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}
</style>
